import {
  AiAutofillAction,
  AiAutofillVariantAction,
  Block,
  BlockType,
  ComponentBlock,
  DocumentBlock,
  EventBlock,
  ListBlock,
  LiteBlock,
  NotificationAction,
  WebhookAction,
  WorkflowAction,
  WorkflowBlock,
  WorkflowBlockChange,
  WorkflowTrigger,
  WorkflowTriggerEvent,
  WorkflowTriggerType,
  getPossibleVariables,
  isCommitWorkflow,
  isComponentBlock,
  isDocumentLikeBlock,
  isEventWorkflow,
  isInstanceBlock,
  isListWorkflow,
  isReferenceBlock,
  isRuntimeWorkflow,
  isWorkflowActionBlockChangeCompatible,
  isWorkflowTriggerBlockChangeCompatible,
  workflowBlockSchema,
} from '@basehub/data/replicache/index'
import Selector, { SelectorItem } from '~/sotano-kit/primitives/selector'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import * as React from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/sotano-kit/primitives/select'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useSyncedValue } from '~/hooks/use-synced-value'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { useTabsStore } from '../../tabs/store'
import {
  GetVariables,
  Variable,
  VariablesInput,
} from '~/components/variables/single-line-input'
import { getId } from '@basehub/utils/id/index'
import { invariant } from '@epic-web/invariant'
import type { TreeManager, TreeNode } from '@basehub/utils/tree/index'
import {
  BellIcon,
  CaretSortIcon,
  ChevronRightIcon,
  CommitIcon,
  PlusIcon,
  Cross2Icon,
  Pencil1Icon,
  ChatBubbleIcon,
} from '@radix-ui/react-icons'
import { cva } from 'class-variance-authority'
import { WebhookIcon } from '~/components/icons/webhook'
import { Button, ButtonLink } from '~/sotano-kit/primitives/button'
import { useEditorMode } from '../../editor-mode-provider'
import { useEditorStore } from '../../editor-store'
import { BlockIcon } from '~/components/block-icon'
import { clsx } from 'clsx'
import { type RepoStore, useRepoStore } from '../../repo-store'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { useBranchOrCommitSegment } from '~/lib/commits/use-selected-branch-or-commit'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { useRepoMembers } from '~/hooks-data/use-repo-members'
import { ComboboxOption } from '~/hooks/use-combobox-state'
import {
  makeFullNameFromClerkUser,
  makeShortNameFromClerkUser,
} from '@basehub/data/auth/utils/make-full-name-from-clerk-user'
import { BaseHubAvatar } from '~/components/basehub-avatar'
import { isHotkey } from 'is-hotkey'
import { TextArea } from '~/sotano-kit/primitives/textarea'
import { SYSTEM_AUTHORS } from '~/lib/system-authors'
import { isValidLayoutBlock } from '../preview-block-switch'
import { getBlockDisplayData } from '@basehub/data/replicache/mutators/repo-space/block/helpers/component-helpers'
import { useTreeSlice } from '~/hooks/use-tree-slice'
import { AIBadge } from '~/sotano-kit/components/date/ai-badge'
import { PublicUserData } from '~/hooks-data/use-user-data'
import { useBaseHubAgents } from './use-basehub-agents'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'

const $selectTrigger = cva(
  '[&>span]:flex [&>span]:gap-x-[0.4375rem] [&>span]:items-center h-8 col-start-1',
  {
    variants: {
      type: {
        pill: 'shadow-floating-pill !bg-dark-bg !text-dark-bright !h-6 text-xs font-medium !px-1',
      },
    },
  }
)
const $selectItem = cva(
  '[&>:last-child]:flex [&>:last-child]:gap-x-[0.4375rem] [&>:last-child]:items-center [&>:last-child]:w-full',
  {
    variants: {
      type: {
        ai: '[&>:last-child]:justify-between',
        default: '',
        pill: 'shadow-floating-pill !bg-dark-bg',
      },
    },
    defaultVariants: {
      type: 'default',
    },
  }
)
const $label = cva('leading-8 text-dark-bright text-base font-medium')

type TriggerOption = {
  label: string
  value: string
  type: WorkflowTriggerType
  blockId?: string
  keywords?: string[]
  hidden?: Block['hidden']
  targetBlockId?: ListBlock['typeOptions']['targetBlockId']
}

const TriggerIcon = ({
  type,
  className,
}: {
  type: WorkflowTrigger['type']
  className?: string
}) => {
  switch (type) {
    case 'commit':
      return <CommitIcon className={className} />
    case 'event-block':
      return <BlockIcon block={{ type: 'event' }} iconProps={{ className }} />
    case 'layout-block':
      return <Pencil1Icon className={className} />
    case 'list-block':
      return <BlockIcon block={{ type: 'list' }} iconProps={{ className }} />
    default:
      return (
        <span className="text-xxs mx-3">
          No icon configured for this trigger
        </span>
      )
  }
}

const TriggerEventIcon = ({
  type,
  className,
}: {
  type: WorkflowTriggerEvent | BlockType
  className?: string
}) => {
  switch (type) {
    case 'created':
      return <PlusIcon className={className} />
    case 'updated':
      return <Pencil1Icon className={className} />
    case 'deleted':
      return <Cross2Icon className={className} />
    default:
      return (
        <span className="text-xxs mx-3">
          No icon configured for this trigger event
        </span>
      )
  }
}

const EVENT_ORDER = { created: -3, updated: -2, deleted: -1 }
const EVENTS_OPTIONS = () => [
  { label: 'Row created', value: 'created' } as const,
  { label: 'Row updated', value: 'updated' } as const,
  { label: 'Row deleted', value: 'deleted' } as const,
]

export const WorkflowBlockInput = ({
  block,
  path,
}: {
  block: WorkflowBlock
  path: string
}) => {
  const replicache = useRepoReplicache().data?.replicache
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const hasVariants = React.useMemo(() => {
    if (!getNode) return false
    if (block.value.trigger?.type !== 'layout-block') return false
    if (!block.value.trigger.layoutBlockId) return false

    const triggerBlock = getNode(block.value.trigger.layoutBlockId)?.value
    if (!triggerBlock) {
      return false
    }
    if (!isDocumentLikeBlock(triggerBlock)) {
      return false
    }
    if (triggerBlock.type === 'instance') {
      const targetBlock = getNode(triggerBlock.typeOptions.targetBlockId)?.value
      if (!targetBlock) return false
      invariant(
        isComponentBlock(targetBlock),
        'Expected instance target to be a component block'
      )
      if (targetBlock.type !== 'component') return false
      return (targetBlock.constraints?.acceptedVariantSets?.length ?? 0) > 0
    }
    return (triggerBlock.constraints?.acceptedVariantSets?.length ?? 0) > 0
  }, [block.value.trigger, getNode])

  const handleActionTypeChange = React.useCallback(
    async (newType: string, key: string) => {
      if (!replicache || !newType) return
      const type = newType as WorkflowAction['type']
      let action: WorkflowAction
      switch (type) {
        case 'webhook':
          action = { type, url: '' }
          break
        case 'notification':
          action = { type: 'notification', userIds: [] }
          break
        case 'ai-autofill':
          action = { type, blockId: null, prompt: '', agentId: 'START' }
          break
        case 'ai-autofill-variant':
          action = { type, blockId: null, prompt: '', agentId: 'START' }
          break
      }
      const params: UpdateBlockParams = {
        id: block.id,
        data: {
          type: 'workflow',
          value: {
            actions: { [key]: action },
          },
        },
        instanceChildTarget: block.instanceChildTarget,
        strategy: 'merge-deep',
        isoNow: new Date().toISOString(),
        path,
      }
      const { undo } = await replicache.mutate.updateBlock(params)
      if (undo) {
        undoManager.current?.push({
          redo: [{ op: 'updateBlock', args: params }],
          undo: [
            {
              op: 'blockBatchMutations',
              args: { mutations: undo },
            },
          ],
        })
      }
    },
    [block.id, block.instanceChildTarget, path, replicache, undoManager]
  )

  const actionsArray = React.useMemo(
    () =>
      Object.entries(block.value.actions)
        .filter(([_, _action]) => {
          let actionIsCompatible = false

          if (_action.type === 'webhook') {
            actionIsCompatible = true
          } else {
            actionIsCompatible =
              ((_action.type === 'ai-autofill' ||
                _action.type === 'ai-autofill-variant') &&
                block.value.trigger.type === 'layout-block') ||
              (_action.type === 'notification' &&
                block.value.trigger.type !== 'layout-block')
          }

          return actionIsCompatible
        })
        .sort(([aKey], [bKey]) => aKey.localeCompare(bKey)),
    [block.value.actions, block.value.trigger.type]
  )

  const hasSingleAction = actionsArray.length <= 1

  return (
    <div
      className="rounded-md border border-dark-border-solid bg-dark-base p-4 gap-4 flex flex-col focus-visible:outline-none focus-visible:border-dark-control-base"
      data-block-id={idToSourceBlockId(block)}
      data-workflow-block
    >
      <div className="grid grid-cols-[3.75rem_1fr] gap-x-6 gap-y-8">
        {/* trigger */}
        <div className={$label()}>When</div>
        <TriggerSelector block={block} path={path} />

        {/* action */}
        <div className={$label({ className: 'col-start-1' })}>Do this</div>
        <div className="flex flex-col gap-y-3 justify-center">
          {actionsArray.map(([key, action]) => {
            const disabledNotify = actionsArray.some(
              ([iterationKey, a]) =>
                a.type === 'notification' && key !== iterationKey
            )

            return (
              <div
                key={key}
                className="grid gap-2 col-start-2"
                style={{
                  gridTemplateColumns: 'clamp(100px,100%,180px) auto 1fr auto',
                }}
              >
                <Select
                  value={action.type}
                  onValueChange={(v) => handleActionTypeChange(v, key)}
                >
                  <SelectTrigger iconRight className={$selectTrigger()}>
                    <SelectValue placeholder="Select an action" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="webhook" className={$selectItem()}>
                      <WebhookIcon />
                      Webhook
                    </SelectItem>
                    {block.value.trigger?.type === 'layout-block' ? (
                      <>
                        <SelectItem
                          value="ai-autofill"
                          className={$selectItem({ type: 'ai' })}
                        >
                          <Pencil1Icon style={{ color: 'inherit' }} />
                          Autofill
                          <AIBadge className="ml-auto" />
                        </SelectItem>
                        <SelectItem
                          value="ai-autofill-variant"
                          disabled={!hasVariants}
                          className={$selectItem({ type: 'ai' })}
                        >
                          <BlockIcon
                            block={{ type: 'variant-set' }}
                            iconProps={{ style: { color: 'inherit' } }}
                          />
                          Apply variant
                          <AIBadge className="ml-auto" />
                        </SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem
                          disabled={disabledNotify}
                          value="notification"
                          className={$selectItem()}
                        >
                          <BellIcon />
                          Notify
                        </SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>

                <HorizontalSeparator />
                <ActionInputSwitch
                  action={action}
                  actionKey={key}
                  block={block}
                  path={path}
                  isSingleAction={hasSingleAction}
                />
                {!hasSingleAction && (
                  <IconButton
                    className="size-8 rounded-md text-dark-faint hover:text-dark-bright -ml-1 -mr-2"
                    tooltip={{
                      content:
                        actionsArray.length === 1
                          ? 'One action required'
                          : 'Remove action',
                    }}
                    style={{ gridColumn: 4, gridRow: 1 }}
                    onClick={async () => {
                      if (!replicache) return
                      if (actionsArray.length === 1) return

                      const newActions = { ...block.value.actions }
                      delete newActions[key]

                      const params: UpdateBlockParams = {
                        id: block.id,
                        data: {
                          type: 'workflow',
                          value: {
                            ...block.value,
                            actions: newActions,
                          },
                        },
                        instanceChildTarget: block.instanceChildTarget,
                        strategy: 'merge',
                        isoNow: new Date().toISOString(),
                        path,
                      }
                      const { undo } =
                        await replicache.mutate.updateBlock(params)
                      undoManager.current?.push({
                        redo: [
                          {
                            op: 'focus',
                            args: {
                              focus: {
                                type: 'block:value',
                                blockId: idToSourceBlockId({
                                  id: block.id,
                                  sourceBlockId: block.sourceBlockId,
                                }),
                              },
                              preferredTabKey: activeTabKey,
                            },
                          },
                          { op: 'updateBlock', args: params },
                        ],
                        undo: [
                          {
                            op: 'focus',
                            args: {
                              focus: {
                                type: 'block:value',
                                blockId: idToSourceBlockId({
                                  id: block.id,
                                  sourceBlockId: block.sourceBlockId,
                                }),
                              },
                              preferredTabKey: activeTabKey,
                            },
                          },
                          {
                            op: 'blockBatchMutations',
                            args: { mutations: undo },
                          },
                        ],
                      })
                    }}
                  >
                    <Cross2Icon className="size-4" />
                  </IconButton>
                )}
              </div>
            )
          })}
          <Button
            onClick={async () => {
              if (!replicache) return
              // parseInt will grab all the numbers before the id
              // e.g: 123-HEX6bfk -> 123
              const lastIdSortingNumber = parseInt(
                Object.keys(block.value.actions).at(-1) ?? ''
              )
              const compoundId =
                String(
                  isNaN(lastIdSortingNumber) ? 0 : lastIdSortingNumber + 1
                ) +
                '-' +
                getId()

              const defaultAction: WorkflowAction = {
                type: 'webhook',
                url: '',
              }

              const params: UpdateBlockParams = {
                id: block.id,
                data: {
                  type: 'workflow',
                  value: {
                    actions: {
                      [compoundId]: defaultAction,
                    },
                  },
                },
                instanceChildTarget: block.instanceChildTarget,
                path,
                strategy: 'merge-deep',
                isoNow: new Date().toISOString(),
              }
              const { undo } = await replicache.mutate.updateBlock(params)
              if (undo) {
                undoManager.current?.push({
                  redo: [{ op: 'updateBlock', args: params }],
                  undo: [
                    { op: 'blockBatchMutations', args: { mutations: undo } },
                  ],
                })
              }
            }}
            unstyled
            focusStyled
            className="text-xs max-w-max text-dark-muted hover:text-dark-dim cursor-default flex items-center gap-x-1 disabled:text-dark-muted"
          >
            <PlusIcon />
            Add another
          </Button>
        </div>
      </div>
    </div>
  )
}

const WebhookInput = ({
  block,
  path,
  actionKey,
  action,
  treeManager,
  className,
}: {
  block: WorkflowBlock
  path: string
  actionKey: string
  action: WebhookAction
  treeManager: TreeManager<Block, NonNullable<unknown>>
  className?: string
}) => {
  const replicache = useRepoReplicache().data?.replicache
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const nodeBlock = useTreeSlice(treeManager, (s) => s.getNode(block.id))
  const getVariables: GetVariables = React.useCallback(
    (segments) => {
      if (!nodeBlock) return []
      return getPossibleVariables({
        segments,
        nodeBlock,
        treeManager: treeManager as TreeManager<LiteBlock>,
        valueKey: 'value',
        selectedVariants: null,
      })
        .filter((v) => !v.hidden)
        .map((v) => {
          return {
            info: v.value,
            label: v.name,
            displayLabel: v.displayLabel,
          } satisfies Variable
        })
    },
    [nodeBlock, treeManager]
  )

  const { onChange, value } = useSyncedValue<unknown>({
    value: action.url,
    customEventsKey: `webhook-input-sync-${idToSourceBlockId(block)}`,
    setValue: React.useCallback(
      async (v, { internalValueRef, customEventsKey }) => {
        if (!replicache?.mutate) return
        const getParams = (v: string): UpdateBlockParams => ({
          id: block.id,
          data: {
            type: 'workflow',
            value: {
              actions: {
                [actionKey]: {
                  type: 'webhook',
                  url: v,
                },
              },
            },
          },
          path,
          instanceChildTarget: block.instanceChildTarget,
          strategy: 'merge-deep',
          isoNow: new Date().toISOString(),
        })
        const { undo } = await replicache.mutate.updateBlock(getParams(v))
        if (undo) {
          undoManager.current?.push({
            redo: [
              {
                op: 'focus',
                args: {
                  focus: {
                    type: 'block:value',
                    blockId: idToSourceBlockId({
                      id: block.id,
                      sourceBlockId: block.sourceBlockId,
                    }),
                  },
                  preferredTabKey: activeTabKey,
                },
              },
              { op: 'updateBlock', args: getParams(internalValueRef.current) },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
            undo: [
              {
                op: 'focus',
                args: {
                  focus: {
                    type: 'block:value',
                    blockId: idToSourceBlockId({
                      id: block.id,
                      sourceBlockId: block.sourceBlockId,
                    }),
                  },
                  preferredTabKey: activeTabKey,
                },
              },
              { op: 'blockBatchMutations', args: { mutations: undo } },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
          })
        }
      },
      [
        actionKey,
        activeTabKey,
        block.id,
        block.instanceChildTarget,
        block.sourceBlockId,
        path,
        replicache?.mutate,
        undoManager,
      ]
    ),
    rateLimitMs: 1000,
  })

  return (
    <VariablesInput
      placeholder="URL"
      value={value}
      setValue={onChange}
      className={clsx(
        'w-full overflow-hidden text-xs h-8 [&_.cm-scroller]:mr-1 [&_.cm-content]:flex [&_.cm-content]:items-center [&_.cm-content]:max-w-full [&_.cm-editor]:!rounded-lg',
        className
      )}
      getVariables={(...args) => {
        const vars = getVariables(...args)
        return vars
      }}
    />
  )
}

type MemberOption = ComboboxOption & { username: string }

const NotifyInput = ({
  action,
  actionKey,
  block,
  path,
  className,
}: {
  action: NotificationAction
  actionKey: string
  block: WorkflowBlock & {
    value: { trigger: { type: 'list-block' | 'commit' | 'event-block' } }
  }
  path: string
  className?: string
}) => {
  const { data: repoMembers } = useRepoMembers()
  const replicache = useRepoReplicache().data?.replicache
  const undoManager = useRepoUndoManager()
  const setSelectedBlockId = useEditorStore((s) => s.setSelectedBlockId)
  const handleMemberSelect = (member: MemberOption) => {
    const existingUserIds = action.userIds

    replicache?.mutate.updateBlock({
      id: block.id,
      data: {
        type: 'workflow',
        value: {
          actions: {
            [actionKey]: {
              type: 'notification',
              userIds: [...existingUserIds, member.value],
            },
          },
        },
      },
      instanceChildTarget: block.instanceChildTarget,
      path,
      strategy: 'merge-deep',
      isoNow: new Date().toISOString(),
    })
  }

  const membersOptions = React.useMemo(() => {
    return (
      repoMembers?.map((m) => ({
        label: makeFullNameFromClerkUser(m),
        value: m.id,
        username: m.username ?? m.email ?? '',
        keywords: [m.username, m.email].filter(Boolean),
      })) ?? []
    )
  }, [repoMembers])
  const availableMembersOptions = React.useMemo(
    () => () => {
      return membersOptions.filter(
        (o) =>
          !action.userIds.includes(o.value) &&
          o.value !== SYSTEM_AUTHORS.START.id &&
          o.value !== SYSTEM_AUTHORS.GHOST.id &&
          o.value !== SYSTEM_AUTHORS.UNKNOWN.id &&
          o.value !== SYSTEM_AUTHORS.API_TOKEN.id
      )
    },
    [action.userIds, membersOptions]
  )

  const handleRemoveMember = async (
    event: React.MouseEvent | React.KeyboardEvent,
    memberId: string
  ) => {
    event.stopPropagation()
    if (!replicache) return
    setSelectedBlockId(idToSourceBlockId(block))
    const params: UpdateBlockParams = {
      id: block.id,
      data: {
        type: 'workflow',
        value: {
          actions: {
            [actionKey]: {
              type: 'notification',
              userIds:
                memberId === 'all'
                  ? []
                  : action.userIds.filter((id) => id !== memberId),
            },
          },
        },
      },
      path,
      instanceChildTarget: block.instanceChildTarget,
      strategy: 'merge-deep',
      isoNow: new Date().toISOString(),
    }

    const { undo } = await replicache.mutate.updateBlock(params)
    if (undo) {
      undoManager.current?.push({
        redo: [{ op: 'updateBlock', args: params }],
        undo: [{ op: 'blockBatchMutations', args: { mutations: undo } }],
      })
    }
  }

  const [search, setSearch] = React.useState('')

  const actionUsers = React.useMemo(() => {
    const users: PublicUserData[] = []

    action.userIds.forEach((id) => {
      const member = repoMembers?.find((m) => m.id === id)
      if (!member) return
      users.push(member)
    })

    return users
  }, [action.userIds, repoMembers])

  return (
    <Selector<MemberOption>
      sideOffset={8}
      extraWidth={4}
      search={search}
      setSearch={setSearch}
      placeholder={!actionUsers.length ? 'Select BaseHub users' : undefined}
      icon={
        <span className="size-5 flex items-center justify-center pointer-events-none">
          <CaretSortIcon className="text-dark-muted pointer-events-none" />
        </span>
      }
      id={`${idToSourceBlockId(block)}-notify-action`}
      size="sm"
      inputClassName="py-[0.1875rem] !gap-y-1"
      className={className}
      disabled={!repoMembers}
      onFocus={() => setSelectedBlockId(idToSourceBlockId(block))}
      options={availableMembersOptions}
      onKeyDown={(e) => {
        if (e.target instanceof HTMLInputElement && e.target.value) return
        if (!action.userIds.at(-1)) return
        if (isHotkey(['delete', 'backspace'], e)) {
          handleRemoveMember(e, action.userIds.at(-1)!)
        } else if (isHotkey(['mod+delete', 'mod+backspace'], e)) {
          handleRemoveMember(e, 'all')
        }
      }}
      onSelect={handleMemberSelect}
      renderResult={() => {
        return actionUsers.map((user) => {
          const name = makeShortNameFromClerkUser(user)
          return (
            <div
              key={user.id}
              className="cursor-default flex items-center gap-x-1 rounded -ml-[3px] border border-dark-border-solid bg-dark-bg px-1 h-6 max-w-[144px] overflow-hidden"
            >
              <BaseHubAvatar
                withBorder
                size="xsmall"
                id={user.id}
                name={name}
              />
              <span className="text-xs text-dark-bright leading-normal truncate">
                {name}
              </span>
              <IconButton
                tooltip={{ content: 'Remove' }}
                className="text-dark-muted !size-4 !p-0"
                onPointerDown={(e) => handleRemoveMember(e, user.id)}
              >
                <Cross2Icon className="size-3" />
              </IconButton>
            </div>
          )
        })
      }}
    >
      {({ search, options, getOptionProps }) => {
        if (!options.length) {
          return (
            <div className="text-xs text-dark-muted text-center leading-normal py-[6.25px]">
              {search
                ? 'There are no members that match your search.'
                : 'No members yet.'}
            </div>
          )
        }

        return options.map((option, index) => {
          return (
            <SelectorItem
              key={option.value}
              {...getOptionProps(index)}
              className="grid grid-rows-2 grid-cols-[auto_1fr] gap-x-2 scroll-mt-1 relative py-1.5 cursor-default select-none items-center rounded px-2 text-xs leading-none outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
            >
              <BaseHubAvatar
                withBorder
                size="medium"
                className="row-span-full"
                id={option.value}
                name={option.value}
              />

              <span className="text-xs font-medium text-dark-bright leading-normal truncate">
                {option.label}
              </span>
              <span className="truncate text-xs text-dark-faint leading-normal">
                {option.username}
              </span>
            </SelectorItem>
          )
        })
      }}
    </Selector>
  )
}

const AutofillInput = ({
  action,
  actionKey,
  block,
  path,
  treeManager,
  kind = 'block',
  isSingleAction,
}: {
  action: AiAutofillAction | AiAutofillVariantAction
  actionKey: string
  block: WorkflowBlockChange
  path: string
  treeManager: TreeManager<Block, NonNullable<unknown>>
  kind?: 'block' | 'variant'
  isSingleAction?: boolean
}) => {
  const replicache = useRepoReplicache().data?.replicache
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const { encodedSegment } = useBranchOrCommitSegment()
  const addTab = useTabsStore((s) => s.addTab)
  const setFocus = useRepoStore((s) => s.setFocus)
  const undoManager = useRepoUndoManager()
  const setSelectedBlockId = useEditorStore((s) => s.setSelectedBlockId)
  const selectedLayoutBlockId = block.value.trigger.layoutBlockId
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const sourceBlockId = idToSourceBlockId(block)
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const selectedLayoutBlock = useWorkingTreeStore((s) => {
    if (!s.tree || !block.value.trigger.layoutBlockId) return undefined
    return s.tree.getNode(block.value.trigger.layoutBlockId)?.value
  })
  const { onChange, ref, value } = useSyncedValue<HTMLTextAreaElement>({
    value: action.prompt,
    customEventsKey: `webhook-input-sync-${idToSourceBlockId(block)}`,
    setValue: React.useCallback(
      async (v, { internalValueRef, customEventsKey }) => {
        if (!replicache) return

        const getParams = (v: string): UpdateBlockParams => ({
          id: block.id,
          data: {
            type: 'workflow',
            value: {
              actions: {
                [actionKey]: {
                  ...action,
                  prompt: v,
                },
              },
            },
          },
          instanceChildTarget: block.instanceChildTarget,
          path,
          strategy: 'merge-deep',
          isoNow: new Date().toISOString(),
        })
        const { undo } = await replicache.mutate.updateBlock(getParams(v))

        if (undo) {
          undoManager.current?.push({
            redo: [
              {
                op: 'focus',
                args: {
                  focus: {
                    type: 'block:wrapper',
                    blockId: sourceBlockId,
                  },
                  preferredTabKey: activeTabKey,
                },
              },
              {
                op: 'updateBlock',
                args: getParams(internalValueRef.current),
              },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
            undo: [
              {
                op: 'focus',
                args: {
                  focus: {
                    type: 'block:wrapper',
                    blockId: sourceBlockId,
                  },
                  preferredTabKey: activeTabKey,
                },
              },
              {
                op: 'blockBatchMutations',
                args: { mutations: undo },
              },

              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
          })
        }
      },
      [
        action,
        actionKey,
        activeTabKey,
        block.id,
        block.instanceChildTarget,
        path,
        replicache,
        sourceBlockId,
        undoManager,
      ]
    ),
    enabled: !!replicache,
    rateLimitMs: 1000,
  })

  const options = React.useCallback(() => {
    if (!block.value.trigger.layoutBlockId) return []
    const layoutBlock = treeManager.getNode(block.value.trigger.layoutBlockId, {
      required: true,
    }).value
    invariant(
      isValidLayoutBlock(layoutBlock),
      `Invalid layout block, found type ${layoutBlock.type} for block with id: ${layoutBlock.id}`
    )
    invariant(
      !isReferenceBlock(layoutBlock),
      `Invalid block selected to trigger workflow, found type reference for block with id: ${layoutBlock.id}`
    )
    invariant(
      !isInstanceBlock(layoutBlock),
      `Invalid block selected to trigger workflow, found type instance for block with id: ${layoutBlock.id}`
    )
    if (action.type === 'ai-autofill') {
      const options = treeManager
        .getChildrenKeys(layoutBlock.id)
        .map((childId) => {
          const _block = treeManager.getNode(childId, {
            required: true,
          }).value
          const sourceBlockId = idToSourceBlockId(_block)
          if (!isWorkflowActionBlockChangeCompatible(_block)) return null

          return {
            label: _block.title || 'Untitled',
            value: sourceBlockId,
            type: _block.type,
          }
        })
        .filter(Boolean)

      return options
    } else {
      const options = (layoutBlock.constraints.acceptedVariantSets ?? []).map(
        (id) => {
          const variant = treeManager.getNode(id, { required: true }).value
          const variantName = variant.title || 'Untitled'
          return {
            label: variantName,
            value: id,
            type: 'variant-set' as const,
            keywords: [id, variantName],
          }
        }
      )

      return options
    }
  }, [action.type, block.value.trigger.layoutBlockId, treeManager])

  const [search, setSearch] = React.useState('')

  let placeholder = undefined
  if (!block.value.trigger.layoutBlockId) {
    placeholder = 'First setup your trigger above to continue'
  } else if (getNode) {
    const targetBlock = action.blockId ? getNode(action.blockId) : null
    if (!targetBlock) {
      placeholder =
        kind === 'block'
          ? 'Select block to generate'
          : 'Select variant to generate'
    }
  }

  const agents = useBaseHubAgents()

  return (
    <>
      <Selector<ComboboxOption & { type: BlockType }>
        sideOffset={8}
        extraWidth={4}
        search={search}
        setSearch={setSearch}
        placeholder={placeholder}
        icon={
          <span className="size-5 flex items-center justify-center pointer-events-none">
            <CaretSortIcon className="text-dark-muted pointer-events-none" />
          </span>
        }
        id={`${idToSourceBlockId(block)}-notify-action`}
        size="sm"
        inputClassName="py-[0.1875rem] !gap-y-1"
        disabled={!selectedLayoutBlock}
        onFocus={() => setSelectedBlockId(idToSourceBlockId(block))}
        options={options}
        className={clsx(isSingleAction && 'col-span-2')}
        onSelect={async (option) => {
          await replicache?.mutate.updateBlock({
            id: block.id,
            data: {
              type: 'workflow',
              value: {
                actions: {
                  [actionKey]: {
                    type: action.type,
                    blockId: option.value,
                  },
                },
              },
            },
            instanceChildTarget: block.instanceChildTarget,
            path,
            strategy: 'merge-deep',
            isoNow: new Date().toISOString(),
          })
        }}
        renderResult={(search) => {
          if (search) return null
          if (!action.blockId) return null
          const blockToAutofill = treeManager.getNode(action.blockId)
          if (!blockToAutofill) return null
          if (!selectedLayoutBlockId) return null
          const sourceBlockId = idToSourceBlockId(blockToAutofill.value)
          return (
            <ButtonLink
              href={`/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${selectedLayoutBlockId}#${sourceBlockId}`}
              unstyled
              className="hover:underline text-xs text-dark-bright leading-normal truncate absolute flex gap-x-1.5 items-center"
              tabIndex={-1}
              onClick={() => {
                addTab({ key: selectedLayoutBlockId, partiallyOpen: true })
                setFocus({ blockId: sourceBlockId, type: 'block:wrapper' })
              }}
            >
              <BlockIcon block={blockToAutofill.value} />
              {blockToAutofill.value.title || 'Untitled'}
            </ButtonLink>
          )
        }}
      >
        {({ search, options, getOptionProps, selectedIndex }) => {
          if (!options.length) {
            let emptyStateMessage = (
              <p className="overflow-hidden text-ellipsis text-balance">
                <span className="font-medium">
                  "{selectedLayoutBlock?.title || 'Untitled'}"
                </span>{' '}
                {action.type === 'ai-autofill'
                  ? 'does not have valid children for this action'
                  : 'does not have variants enabled'}
              </p>
            )

            if (search) {
              emptyStateMessage = (
                <p className="overflow-hidden text-ellipsis text-balance">
                  {action.type === 'ai-autofill'
                    ? 'No compatible children found for'
                    : 'No variants found for'}
                  <span className="font-medium"> "{search}"</span>
                </p>
              )
            }

            return (
              <div className="text-xs text-dark-muted text-center leading-normal py-[6.25px] overflow-hidden px-1">
                {emptyStateMessage}
              </div>
            )
          }

          return options.map((option, index) => {
            const isHighlighted = selectedIndex === index
            const isSelected = action.blockId === option.value
            const b = treeManager.getNode(option.value)?.value
            if (!b) return null

            return (
              <SelectorItem
                key={option.value}
                {...getOptionProps(index)}
                className={clsx(
                  isHighlighted && 'bg-dark-shade-hover outline-none',
                  isSelected && 'text-dark-control-highlight font-medium',
                  'scroll-mt-1 flex gap-x-2 h-8 relative py-1.5 cursor-default select-none items-center rounded px-2 text-xs leading-none outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
                )}
              >
                <BlockIcon block={b} />
                <span className="text-xs leading-normal truncate">
                  {option.label}
                </span>
              </SelectorItem>
            )
          })
        }}
      </Selector>

      <div
        className={clsx(
          'col-span-full relative flex flex-col',
          !isSingleAction && 'max-w-[calc(100%-28px)]'
        )}
      >
        <TextArea
          className="!font-normal border border-dark-border-solid w-full pb-10 !px-2 pt-1.5 focus:border-dark-accent z-10 relative"
          rows={4}
          placeholder="Enter your request or prompt..."
          value={value}
          ref={ref}
          onChange={(e) => 'value' in e.target && onChange(e.target.value)}
        />

        <div className="pt-2 px-2 flex items-center gap-x-2 w-full bg-dark-bg rounded-b-xl -mt-2 border-x border-b border-dark-border-solid h-12">
          <Select
            value={action.agentId}
            onValueChange={async (v) => {
              if (!replicache) return
              await replicache?.mutate.updateBlock({
                id: block.id,
                data: {
                  type: 'workflow',
                  value: {
                    actions: {
                      [actionKey]: {
                        type: action.type,
                        agentId: v,
                      },
                    },
                  },
                },
                instanceChildTarget: block.instanceChildTarget,
                path,
                strategy: 'merge-deep',
                isoNow: new Date().toISOString(),
              })
            }}
          >
            <SelectTrigger
              iconRight
              className={$selectTrigger({
                type: 'pill',
                className: 'max-w-[200px]',
              })}
            >
              <SelectValue placeholder="Select an agent" />
            </SelectTrigger>
            <SelectContent>
              {agents.map((a) => {
                const sourceBlockId = idToSourceBlockId(a.block)
                return (
                  <SelectItem
                    value={sourceBlockId}
                    className={$selectItem({
                      className:
                        'flex items-center justify-between relative !pr-8',
                    })}
                    key={sourceBlockId}
                  >
                    <BaseHubAvatar
                      id={sourceBlockId}
                      name={a.block.title || 'Untitled'}
                      forceSrc={a.block.typeOptions.avatar}
                      size="petite"
                    />
                    <span className="truncate">
                      {a.block.title || 'Untitled'}
                    </span>
                    {!a.isSystem && a.diffStatus === 'created' && (
                      <SimpleTooltip
                        content={`This agent needs to be committed before it can be used`}
                        contentClassName="!z-[500]"
                      >
                        <span
                          className={clsx(
                            'size-2 rounded-full absolute right-2 top-1/2 -translate-y-1/2 bg-dark-control-base'
                          )}
                        />
                      </SimpleTooltip>
                    )}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>

          <Select
            value={action.changeType || 'direct'}
            onValueChange={async (v) => {
              if (!replicache) return

              const asd: UpdateBlockParams = {
                id: block.id,
                data: {
                  type: 'workflow',
                  value: {
                    actions: {
                      [actionKey]: {
                        ...action,
                        changeType: v as 'direct',
                      },
                    },
                  },
                },
                instanceChildTarget: block.instanceChildTarget,
                path,
                strategy: 'merge-deep',
                isoNow: new Date().toISOString(),
              }
              const { undo } = await replicache.mutate.updateBlock(asd)

              if (undo) {
                undoManager.current?.push({
                  redo: [{ op: 'updateBlock', args: asd }],
                  undo: [
                    { op: 'blockBatchMutations', args: { mutations: undo } },
                  ],
                })
              }
            }}
          >
            <SelectTrigger
              iconRight
              className={$selectTrigger({ type: 'pill' })}
            >
              <SelectValue placeholder="Select a change type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="direct" className={$selectItem()}>
                <Pencil1Icon />
                Edit content directly
              </SelectItem>
              <SelectItem value="suggestion" className={$selectItem()}>
                <ChatBubbleIcon />
                Make a suggestion
              </SelectItem>
              <SelectItem value="auto-commit" className={$selectItem()}>
                <CommitIcon />
                Edit content and commit
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </>
  )
}

const ActionInputSwitch = ({
  action,
  actionKey,
  block,
  path,
  isSingleAction,
}: {
  action: WorkflowAction
  actionKey: string
  block: WorkflowBlock
  path: string
  isSingleAction: boolean
}) => {
  const treeManager = useWorkingTreeStore((s) => s.tree)
  if (!treeManager) return null

  switch (action.type) {
    case 'webhook': {
      return (
        <WebhookInput
          key={actionKey}
          block={block}
          path={path}
          actionKey={actionKey}
          action={action}
          treeManager={treeManager}
          className={clsx(isSingleAction && 'col-span-2')}
        />
      )
    }
    case 'notification': {
      if (!isCommitWorkflow(block) && !isEventWorkflow(block)) return null

      return (
        <NotifyInput
          action={action}
          actionKey={actionKey}
          block={block}
          path={path}
          className={clsx(isSingleAction && 'col-span-2')}
        />
      )
    }

    case 'ai-autofill':
    case 'ai-autofill-variant': {
      if (!isRuntimeWorkflow(block)) return null

      return (
        <AutofillInput
          action={action}
          actionKey={actionKey}
          block={block}
          path={path}
          treeManager={treeManager}
          kind={action.type === 'ai-autofill-variant' ? 'variant' : 'block'}
          isSingleAction={isSingleAction}
        />
      )
    }
    default:
      return null
  }
}

const HorizontalSeparator = () => {
  return (
    <span className="self-center text-dark-bright size-[14px]">
      <ChevronRightIcon />
    </span>
  )
}

const TriggerMainSelector = ({
  block,
  path,
  className,
}: {
  block: WorkflowBlock
  path: string
  className?: string
}) => {
  const replicache = useRepoReplicache().data?.replicache
  const editorMode = useEditorMode()
  const isReadOnly = editorMode !== 'write:all'
  const treeManagerRef = useWorkingTreeStore((s) => s.treeRef)
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const { encodedSegment } = useBranchOrCommitSegment()
  const addTab = useTabsStore((s) => s.addTab)
  const setFocus = useRepoStore((s) => s.setFocus)
  const treeManager = useWorkingTreeStore((s) => s.tree)
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const undoManager = useRepoUndoManager()
  const sourceBlockId = idToSourceBlockId({
    id: block.id,
    sourceBlockId: block.sourceBlockId,
  })
  const isSelected = useEditorStore((s) => s.selectedBlockId === sourceBlockId)
  const { listBlocks, eventBlocks } = useWorkingTreeStore((s) => {
    const idx = (s.tree?.compoundIndexes.get('blocksByType') ??
      new Map()) as Map<BlockType, Set<string>>
    const listBlocks = [...(idx.get('list') ?? new Set())]
      .map((id) => s.tree?.getNode(id))
      .filter(Boolean) as TreeNode<ListBlock>[]
    const eventBlocks = [...(idx.get('event') ?? new Set())]
      .map((id) => s.tree?.getNode(id))
      .filter(Boolean) as TreeNode<EventBlock>[]
    return { listBlocks, eventBlocks }
  })

  const {
    options,
    value,
  }: {
    options: TriggerOption[]
    value: string
  } = React.useMemo(() => {
    let value = ''
    switch (block.value.trigger?.type) {
      case 'commit':
        value = `root:${block.value.trigger.events[0]}`
        break
      case 'list-block':
        value = `list-block:${block.value.trigger.blockId}`
        break
      case 'event-block':
        value = `event-block:${block.value.trigger.blockId}`
        break
      case 'layout-block':
        value = `layout-block:${block.value.trigger.events[0]}`
        break
      default:
        break
    }
    return {
      options: [
        {
          label: 'A commit happens',
          value: 'commit:created',
          type: 'commit',
        },
        {
          label: 'A block changes',
          value: 'layout-block:updated',
          type: 'layout-block',
        },
        ...listBlocks.map((b) => {
          return {
            label: b.value.title || 'Untitled',
            value: `list-block:${idToSourceBlockId(b.value)}`,
            type: 'list-block',
            blockId: idToSourceBlockId(b.value),
            targetBlockId: b.value.typeOptions.targetBlockId,
            hidden: b.value.hidden,
          } satisfies TriggerOption
        }),
        ...eventBlocks.map((b) => {
          const parentNode = b.parentKey
            ? treeManager?.getNode(b.parentKey)
            : null
          return {
            label: `${
              parentNode?.value.title ? `${parentNode.value.title} / ` : ''
            }${b.value.title || 'Untitled'}`,
            value: `event-block:${idToSourceBlockId(b.value)}`,
            type: 'event-block',
            blockId: idToSourceBlockId(b.value),
            hidden: parentNode?.value.hidden,
          } satisfies TriggerOption
        }),
      ] satisfies TriggerOption[],
      value,
    }
  }, [block.value.trigger, eventBlocks, listBlocks, treeManager])

  const renderResult = React.useCallback(
    (search: string) => {
      if (search) return null
      if (!block.value.trigger) return null

      const trigger = block.value.trigger
      const optionMatch = options.find(
        (o) =>
          o.value ===
          `${trigger.type}:${
            'blockId' in trigger ? trigger.blockId : trigger.events[0]
          }`
      )

      if ('blockId' in trigger) {
        let blockId = trigger.blockId
        let focus: RepoStore['focus'] = {
          type: 'block:document-title',
          blockId: blockId,
        }
        if (trigger.type === 'event-block') {
          if (!treeManagerRef.current) return null
          const eventNode = treeManagerRef.current.getNode(trigger.blockId)
          if (!eventNode) return null
          if (eventNode) {
            blockId = treeManagerRef.current.getNode(trigger.blockId, {
              required: true,
            }).parentKey as string
            focus = {
              type: 'block:wrapper',
              blockId: trigger.blockId,
            }
          }
        }

        return (
          <ButtonLink
            unstyled
            href={`/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${blockId}`}
            className="flex items-center absolute space-x-2 hover:underline leading-none pr-1.5"
            tabIndex={-1}
            onClick={() => {
              addTab({ key: blockId, partiallyOpen: true })
              setFocus(focus)
            }}
          >
            {optionMatch && (
              <span className="relative">
                <TriggerIcon type={optionMatch.type} />
              </span>
            )}
            <span>{optionMatch?.label || 'Untitled'}</span>
          </ButtonLink>
        )
      }

      return (
        <div className="flex items-center absolute space-x-2 leading-none pr-1.5">
          {optionMatch && (
            <span className="relative">
              <TriggerIcon type={optionMatch.type} />
            </span>
          )}
          <span>{optionMatch?.label || 'Untitled'}</span>
        </div>
      )
    },
    [
      addTab,
      block.value.trigger,
      encodedSegment,
      options,
      repoSlug,
      setFocus,
      teamSlug,
      treeManagerRef,
    ]
  )

  const onSelect = React.useCallback(
    async (v: TriggerOption) => {
      if (!replicache) return
      if (v.value === value) return // no change
      const [type, id] = v.value.split(':')
      let updates: WorkflowBlock['value']['trigger'] | undefined

      switch (type) {
        case 'commit':
          updates = {
            type: 'commit',
            events: ['created'],
          }
          break
        case 'list-block': {
          invariant(id, 'list-block must have an id')
          updates = {
            type: 'list-block',
            blockId: id,
            events: ['created'],
          }
          break
        }
        case 'event-block': {
          invariant(id, 'event-block must have an id')
          updates = {
            type: 'event-block',
            blockId: id,
            events: ['created'],
          }
          break
        }
        case 'layout-block': {
          updates = {
            type: 'layout-block',
            layoutBlockId: null,
            blockIds: [],
            events: ['updated'],
          }
          break
        }
        default:
          return null
      }
      const newActions = { ...(block.value.actions ?? {}) }
      const lastIdSortingNumber = parseInt(
        Object.keys(block.value.actions).at(-1) ?? ''
      )
      const compoundId =
        String(isNaN(lastIdSortingNumber) ? 0 : lastIdSortingNumber + 1) +
        '-' +
        getId()
      if (type === 'layout-block') {
        const hasCompatibleAction = Object.values(block.value.actions).some(
          (action) =>
            action.type === 'ai-autofill' ||
            action.type === 'ai-autofill-variant' ||
            action.type === 'webhook'
        )
        if (!hasCompatibleAction) {
          newActions[compoundId] = {
            type: 'ai-autofill',
            blockId: null,
            prompt: '',
            agentId: 'START',
          } satisfies AiAutofillAction
        }
      } else {
        const hasCompatibleAction = Object.values(block.value.actions).some(
          (action) =>
            action.type === 'webhook' || action.type === 'notification'
        )
        if (!hasCompatibleAction) {
          newActions[compoundId] = {
            type: 'webhook',
            url: '',
          } satisfies WebhookAction
        }
      }

      const params: UpdateBlockParams = {
        id: block.id,
        data: {
          type: 'workflow',
          value: { trigger: updates, actions: newActions },
        },
        instanceChildTarget: block.instanceChildTarget,
        path,
        strategy: 'merge-deep',
        isoNow: new Date().toISOString(),
      }

      hasUpdated.current = true
      const { undo } = await replicache.mutate.updateBlock(params)

      if (undo) {
        undoManager.current?.push({
          redo: [
            {
              op: 'focus',
              args: {
                focus: {
                  type: 'block:value',
                  blockId: sourceBlockId,
                },
                preferredTabKey: activeTabKey,
              },
            },
            { op: 'updateBlock', args: params },
          ],
          undo: [
            {
              op: 'focus',
              args: {
                focus: {
                  type: 'block:value',
                  blockId: sourceBlockId,
                },
                preferredTabKey: activeTabKey,
              },
            },
            { op: 'blockBatchMutations', args: { mutations: undo } },
          ],
        })
      }
    },
    [
      activeTabKey,
      block.id,
      block.instanceChildTarget,
      block.value.actions,
      path,
      replicache,
      sourceBlockId,
      undoManager,
      value,
    ]
  )

  const hasUpdated = React.useRef(false)
  React.useEffect(() => {
    if (!hasUpdated.current || !isSelected) {
      hasUpdated.current = false
      return
    }

    const nextInput = document.getElementById(`${sourceBlockId}-second-input`)

    if (nextInput) {
      nextInput.focus()
    }
    hasUpdated.current = false
  }, [block.value.trigger.type, isSelected, sourceBlockId])

  const [search, setSearch] = React.useState('')

  return (
    <Selector
      sideOffset={8}
      extraWidth={4}
      search={search}
      setSearch={setSearch}
      icon={
        <span className="size-5 flex items-center justify-center pointer-events-none">
          <CaretSortIcon className="text-dark-muted pointer-events-none" />
        </span>
      }
      id={`${idToSourceBlockId(block)}-workflow-input`}
      size="sm"
      inputClassName="!pl-2"
      disabled={isReadOnly}
      readOnly={isReadOnly}
      options={() =>
        options.reduce<Array<TriggerOption>>((acc, current) => {
          if (!current.hidden) acc.push(current)
          return acc
        }, [])
      }
      closeAfterSelect
      onSelect={onSelect}
      renderResult={renderResult}
      className={clsx('flex-1', className)}
    >
      {({ options, getOptionProps, selectedIndex, search }) => {
        if (!options.length) {
          return (
            <div className="text-xs text-dark-muted text-center leading-normal py-[6.25px]">
              No workflow triggers for
              <span className="font-medium">"{search}"</span>
            </div>
          )
        }

        return options.map((option, index) => {
          const isHighlighted = selectedIndex === index
          let isSelected = false
          if (!block.value.trigger) {
            isSelected = false
          } else if (
            block.value.trigger.type === 'commit' ||
            block.value.trigger.type === 'layout-block'
          ) {
            isSelected = option.type === block.value.trigger?.type
          } else {
            isSelected =
              block.value.trigger.type + ':' + block.value.trigger.blockId ===
              option.value
          }

          let typeOption

          const previousOption = index > 0 ? options[index - 1] : null
          const shouldRenderTypeOption = option.type !== previousOption?.type
          if (shouldRenderTypeOption) {
            switch (option.type) {
              case 'list-block':
                typeOption = 'Collections'
                break
              case 'event-block':
                typeOption = 'Events'
                break
              case 'layout-block':
                typeOption = 'Runtime'
                break
              default:
                typeOption = 'Root'
            }
          }

          const iconClassName = clsx(
            isSelected ? 'text-dark-control-highlight' : 'text-dark-bright'
          )

          return (
            <div key={option.value}>
              {shouldRenderTypeOption && (
                <>
                  {index !== 0 && (
                    <hr className="my-1 border-dark-border-solid" />
                  )}
                  {typeOption && (
                    <span className="ml-2 text-dark-faint text-xs font-medium">
                      {typeOption}
                    </span>
                  )}
                </>
              )}
              <SelectorItem
                {...getOptionProps(index)}
                className={clsx(
                  isHighlighted && 'bg-dark-shade-hover outline-none',
                  isSelected && 'text-dark-control-highlight font-medium',
                  'scroll-mt-1 relative flex h-8 cursor-default select-none items-center rounded px-3 py-1.5 text-xs leading-none outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
                )}
              >
                <span className="relative">
                  <TriggerIcon type={option.type} className={iconClassName} />
                </span>
                <span className="inline-flex items-center w-full ml-2 max-w-full overflow-hidden">
                  {option.label}
                </span>
              </SelectorItem>
            </div>
          )
        })
      }}
    </Selector>
  )
}

const TriggerSelector = ({
  block,
  path,
}: {
  block: WorkflowBlock
  path: string
}) => {
  if (!block.value.trigger) return null

  return (
    <div className="flex flex-col flex-1 gap-2">
      <div className="flex flex-1 gap-2">
        <TriggerMainSelector
          className={clsx(
            (isListWorkflow(block) || isRuntimeWorkflow(block)) &&
              'max-w-[180px]'
          )}
          block={block}
          path={path}
        />
        {(isListWorkflow(block) || isRuntimeWorkflow(block)) && (
          <>
            <HorizontalSeparator />
            {isListWorkflow(block) ? (
              <WorkflowBlockTriggerEventSelector block={block} path={path} />
            ) : (
              <WorkflowBlockTriggerBlockSelector block={block} path={path} />
            )}
          </>
        )}
      </div>
      {isRuntimeWorkflow(block) && (
        <div className="col-start-2 flex">
          <WorkflowBlockTriggerBlockChildrenSelector
            block={block}
            path={path}
          />
        </div>
      )}
    </div>
  )
}

const WorkflowBlockTriggerEventSelector = ({
  block,
  path,
}: {
  block: WorkflowBlock & {
    value: { trigger: Extract<WorkflowTrigger, { type: 'list-block' }> }
  }
  path: string
}) => {
  const replicache = useRepoReplicache().data?.replicache
  const undoManager = useRepoUndoManager()
  const handleRemoveEvents = React.useCallback(
    async (e: React.KeyboardEvent, key: string) => {
      e.preventDefault()
      if (!replicache) return
      const prev = block.value.trigger?.events ?? []
      const params: UpdateBlockParams = {
        id: block.id,
        data: {
          type: 'workflow',
          value: {
            trigger: {
              events:
                key === 'all' ? [] : prev.filter((event) => event !== key),
            },
          },
        },
        instanceChildTarget: block.instanceChildTarget,
        path,
        strategy: 'merge-deep',
        isoNow: new Date().toISOString(),
      }
      const { undo } = await replicache.mutate.updateBlock(params)
      if (undo) {
        undoManager.current?.push({
          redo: [{ op: 'updateBlock', args: params }],
          undo: [{ op: 'blockBatchMutations', args: { mutations: undo } }],
        })
      }
    },
    [
      block.id,
      block.instanceChildTarget,
      block.value.trigger?.events,
      path,
      replicache,
      undoManager,
    ]
  )

  const events = block.value.trigger?.events ?? []
  const [search, setSearch] = React.useState('')

  return (
    <Selector
      sideOffset={8}
      extraWidth={4}
      search={search}
      setSearch={setSearch}
      size="sm"
      icon={
        <span className="size-5 flex items-center justify-center pointer-events-none">
          <CaretSortIcon className="text-dark-muted pointer-events-none" />
        </span>
      }
      id={`${idToSourceBlockId(block)}-second-input`}
      options={EVENTS_OPTIONS}
      className="flex-1"
      placeholder={
        !block.value.trigger.events?.length ? 'Select events' : undefined
      }
      closeAfterSelect={false}
      renderResult={() => {
        return events.map((v, index) => (
          <div key={v} className="flex items-center gap-x-1">
            <TriggerEventIcon type={v} />
            <span className="capitalize-first">
              {v}
              {Boolean(index !== events.length - 1) && ', '}
            </span>
          </div>
        ))
      }}
      onSelect={async (v) => {
        if (!replicache) return
        if (!v) return
        const prev = block.value.trigger.events ?? []
        let newEvents
        if (prev.includes(v.value)) {
          newEvents = prev.filter((e) => e !== v.value)
        } else {
          newEvents = (
            [...prev, v.value] as Array<'created' | 'updated' | 'deleted'>
          ).sort((a, b) => EVENT_ORDER[a] - EVENT_ORDER[b])
        }
        const params: UpdateBlockParams = {
          id: block.id,
          data: {
            type: 'workflow',
            value: {
              trigger: workflowBlockSchema.shape.value.shape.trigger.parse({
                ...block.value.trigger,
                events: newEvents,
              }),
            },
          },
          instanceChildTarget: block.instanceChildTarget,
          path,
          strategy: 'merge-deep',
          isoNow: new Date().toISOString(),
        }
        const { undo } = await replicache.mutate.updateBlock(params)
        if (undo) {
          undoManager.current?.push({
            redo: [{ op: 'updateBlock', args: params }],
            undo: [{ op: 'blockBatchMutations', args: { mutations: undo } }],
          })
        }
      }}
      onKeyDown={(e) => {
        if (e.target instanceof HTMLInputElement && e.target.value) {
          return
        }
        if (
          !block.value.trigger ||
          !('events' in block.value.trigger) ||
          !block.value.trigger.events?.at(-1)
        ) {
          return
        }
        if (isHotkey(['delete', 'backspace'], e)) {
          handleRemoveEvents(e, block.value.trigger.events.at(-1)!)
        } else if (isHotkey(['mod+delete', 'mod+backspace'], e)) {
          handleRemoveEvents(e, 'all')
        }
      }}
    >
      {({ options, getOptionProps, selectedIndex, search }) => {
        if (!options.length) {
          return (
            <div className="text-xs text-dark-muted text-center leading-normal py-[6.25px]">
              There are no events matching
              <span className="font-medium">"{search}"</span>
            </div>
          )
        }

        return options.map((option, index) => {
          const isHighlighted = selectedIndex === index
          const isSelected = block.value.trigger?.events?.includes(option.value)

          return (
            <div key={option.value}>
              <SelectorItem
                {...getOptionProps(index)}
                className={clsx(
                  isHighlighted && 'bg-dark-shade-hover outline-none',
                  isSelected && 'text-dark-control-highlight font-medium',
                  'scroll-mt-1 relative flex h-8 cursor-default select-none items-center rounded px-3 py-1.5 text-xs leading-none outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
                )}
              >
                <span className="relative">
                  <TriggerEventIcon
                    type={option.value}
                    className={clsx(
                      isSelected
                        ? 'text-dark-control-highlight'
                        : 'text-dark-bright'
                    )}
                  />
                </span>
                <span className="inline-flex items-center w-full ml-2 max-w-full overflow-hidden">
                  {option.label}
                </span>
              </SelectorItem>
            </div>
          )
        })
      }}
    </Selector>
  )
}

const WorkflowBlockTriggerBlockSelector = ({
  block,
  path,
}: {
  block: WorkflowBlockChange
  path: string
}) => {
  const { documentBlocks, componentBlocks } = useWorkingTreeStore((s) => {
    const idx = (s.tree?.compoundIndexes.get('blocksByType') ??
      new Map()) as Map<BlockType, Set<string>>
    const documentBlocks = [...(idx.get('document') ?? new Set())]
      .map((id) => s.tree?.getNode(id))
      .filter(Boolean) as TreeNode<DocumentBlock>[]
    const componentBlocks = [...(idx.get('component') ?? new Set())]
      .map((id) => s.tree?.getNode(id))
      .filter(Boolean) as TreeNode<ComponentBlock>[]

    return { documentBlocks, componentBlocks }
  })
  const sourceBlockId = idToSourceBlockId(block)
  const isSelected = useEditorStore((s) => s.selectedBlockId === sourceBlockId)
  const selectedLayoutBlockId = block.value.trigger.layoutBlockId
  const selectedLayoutBlock = useWorkingTreeStore((s) => {
    if (!s.tree) return null
    if (!block.value.trigger.layoutBlockId) return null
    return s.tree.getNode(block.value.trigger.layoutBlockId)
  })
  const hasChildren = useWorkingTreeStore((s) => {
    if (!s.tree) return false
    if (!block.value.trigger.layoutBlockId) return false
    return s.tree.getChildrenKeys(block.value.trigger.layoutBlockId).length > 0
  })
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const setFocus = useRepoStore((s) => s.setFocus)
  const { encodedSegment } = useBranchOrCommitSegment()
  const addTab = useTabsStore((s) => s.addTab)
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const getChildrenKeys = useWorkingTreeStore((s) => s.tree?.getChildrenKeys)
  const replicache = useRepoReplicache().data?.replicache
  const undoManager = useRepoUndoManager()
  const handleRemoveBlockId = React.useCallback(
    async (e: React.KeyboardEvent) => {
      e.preventDefault()
      if (!replicache) return
      const params: UpdateBlockParams = {
        id: block.id,
        data: {
          type: 'workflow',
          value: {
            trigger: {
              type: 'layout-block',
              blockIds: [],
              layoutBlockId: null,
              events: ['updated'],
            },
          },
        },
        instanceChildTarget: block.instanceChildTarget,
        path,
        strategy: 'merge-deep',
        isoNow: new Date().toISOString(),
      }
      const { undo } = await replicache.mutate.updateBlock(params)
      if (undo) {
        undoManager.current?.push({
          redo: [{ op: 'updateBlock', args: params }],
          undo: [{ op: 'blockBatchMutations', args: { mutations: undo } }],
        })
      }
    },
    [block.id, block.instanceChildTarget, path, replicache, undoManager]
  )

  const onClear = React.useCallback(() => {
    if (!replicache) return
    if (!selectedLayoutBlockId) return
    const params: UpdateBlockParams = {
      id: block.id,
      data: {
        type: 'workflow',
        value: {
          trigger: {
            ...block.value.trigger,
            layoutBlockId: null,
            blockIds: [],
          },
        },
      },
      instanceChildTarget: block.instanceChildTarget,
      path,
      strategy: 'merge-deep',
      isoNow: new Date().toISOString(),
    }
    replicache.mutate.updateBlock(params)
  }, [
    block.id,
    block.instanceChildTarget,
    block.value.trigger,
    path,
    replicache,
    selectedLayoutBlockId,
  ])

  const hasUpdated = React.useRef(false)
  React.useEffect(() => {
    if (!hasUpdated.current || !isSelected) {
      hasUpdated.current = false
      return
    }

    const nextInput = document.getElementById(`${sourceBlockId}-third-input`)

    if (nextInput) {
      nextInput.focus()
    }
    hasUpdated.current = false
  }, [block.value.trigger.layoutBlockId, isSelected, sourceBlockId])

  const [search, setSearch] = React.useState('')

  return (
    <Selector<ComboboxOption & { type: BlockType; hidden: boolean }>
      sideOffset={8}
      extraWidth={4}
      search={search}
      setSearch={setSearch}
      size="sm"
      icon={
        <span className="size-5 flex items-center justify-center pointer-events-none">
          <CaretSortIcon className="text-dark-muted pointer-events-none" />
        </span>
      }
      id={`${idToSourceBlockId(block)}-second-input`}
      className="flex-1"
      placeholder={!selectedLayoutBlock ? 'Select a layout block' : undefined}
      onRemove={block.value.trigger.layoutBlockId ? onClear : undefined}
      closeAfterSelect
      renderResult={(search) => {
        if (search) return <p className="absolute">{search}</p>
        if (!block.value.trigger.layoutBlockId || !getNode) return null
        if (!selectedLayoutBlock?.value) return null
        const parent = selectedLayoutBlock.parentKey
          ? getNode(selectedLayoutBlock.parentKey)
          : null
        const { displayTitle: title } = getBlockDisplayData({
          block: selectedLayoutBlock.value,
          parentBlock: parent?.value,
        })
        return (
          <ButtonLink
            unstyled
            href={`/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${selectedLayoutBlockId}#${sourceBlockId}`}
            className="flex items-center absolute space-x-1.5 hover:underline leading-none pr-1.5"
            tabIndex={-1}
            onClick={() => {
              if (!selectedLayoutBlockId) return
              addTab({ key: selectedLayoutBlockId, partiallyOpen: true })
              setFocus({ blockId: sourceBlockId, type: 'block:wrapper' })
            }}
          >
            <BlockIcon
              block={{ type: selectedLayoutBlock?.value.type }}
              forceNonEmptyDocumentIcon={hasChildren}
            />
            <span>{title || 'Untitled'}</span>
          </ButtonLink>
        )
      }}
      onSelect={async (v) => {
        if (!replicache) return
        if (!v) return
        if (v.value === block.value.trigger.layoutBlockId) return // no change
        const newActions: WorkflowBlock['value']['actions'] = {}
        Object.entries(block.value.actions).forEach(([key, action]) => {
          switch (action.type) {
            case 'ai-autofill':
              newActions[key] = {
                ...action,
                blockId: null,
              }
              break
            case 'ai-autofill-variant':
              newActions[key] = {
                ...action,
                blockId: null,
              }
              break
            default:
              break
          }
        })
        const params: UpdateBlockParams = {
          id: block.id,
          data: {
            type: 'workflow',
            value: {
              trigger: {
                ...block.value.trigger,
                layoutBlockId: v.value,
                blockIds: [],
              },
              actions: newActions,
            },
          },
          instanceChildTarget: block.instanceChildTarget,
          path,
          strategy: 'merge-deep',
          isoNow: new Date().toISOString(),
        }
        hasUpdated.current = true
        const { undo } = await replicache.mutate.updateBlock(params)
        if (undo) {
          undoManager.current?.push({
            redo: [{ op: 'updateBlock', args: params }],
            undo: [{ op: 'blockBatchMutations', args: { mutations: undo } }],
          })
        }
      }}
      onKeyDown={(e) => {
        if (e.target instanceof HTMLInputElement && e.target.value) return
        if (!block.value.trigger) return
        if (isHotkey(['delete', 'backspace'], e)) {
          handleRemoveBlockId(e)
        } else if (isHotkey(['mod+delete', 'mod+backspace'], e)) {
          handleRemoveBlockId(e)
        }
      }}
      options={() => {
        if (!getNode) return []
        return [...documentBlocks, ...componentBlocks].map((b) => {
          const keywords = [b.value.title, b.value.type]
          if (b.value.type === 'component') {
            keywords.push(
              b.value.typeOptions.displayTitle,
              b.value.typeOptions.displayDescription
            )
          }
          const parent = b.parentKey ? getNode(b.parentKey) : null
          const { displayTitle: title } = getBlockDisplayData({
            block: b.value,
            parentBlock: parent?.value,
          })
          const sourceBlockId = idToSourceBlockId(b.value)

          return {
            label: title,
            value: sourceBlockId,
            keywords,
            type: b.value.type,
            hidden: b.value.hidden,
          }
        })
      }}
    >
      {({ options, getOptionProps, selectedIndex, search }) => {
        if (!options.length) {
          return (
            <p className="text-xs text-dark-muted text-center leading-normal py-[6.25px] text-balance">
              No documents or components matching{' '}
              <span className="font-medium">"{search}"</span>
            </p>
          )
        }

        return options.map((option, index) => {
          const isHighlighted = selectedIndex === index
          const isSelected = option.value === block.value.trigger.layoutBlockId

          const previousOption = index > 0 ? options[index - 1] : null
          const shouldRenderTypeOption = option.type !== previousOption?.type
          let typeOption

          if (shouldRenderTypeOption) {
            switch (option.type) {
              case 'document':
                typeOption = 'Document'
                break
              case 'component':
                typeOption = 'Component (propagates to instances)'
                break
            }
          }

          const b = getNode?.(option.value)?.value
          if (!b) return null

          return (
            <div key={option.value}>
              {shouldRenderTypeOption && (
                <>
                  {index !== 0 && (
                    <hr className="my-1 border-dark-border-solid" />
                  )}
                  {typeOption && (
                    <span className="ml-2 text-dark-faint text-xs font-medium">
                      {typeOption}
                    </span>
                  )}
                </>
              )}
              <SelectorItem
                {...getOptionProps(index)}
                className={clsx(
                  isHighlighted && 'bg-dark-shade-hover outline-none',
                  isSelected && 'text-dark-control-highlight font-medium',
                  'scroll-mt-1 relative flex h-8 cursor-default select-none items-center rounded px-3 py-1.5 text-xs leading-none outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
                )}
              >
                <BlockIcon
                  block={b}
                  forceNonEmptyDocumentIcon={
                    getChildrenKeys
                      ? getChildrenKeys(option.value).length > 0
                      : false
                  }
                  iconProps={{
                    className: clsx(
                      isSelected
                        ? 'text-dark-control-highlight'
                        : 'text-dark-bright',
                      option.hidden && 'text-opacity-50'
                    ),
                  }}
                />
                <span
                  className={clsx(
                    option.hidden && 'opacity-50',
                    'inline-flex items-center w-full ml-2 max-w-full overflow-hidden'
                  )}
                >
                  {option.label}
                </span>
              </SelectorItem>
            </div>
          )
        })
      }}
    </Selector>
  )
}

type ChildrenOption = ComboboxOption & { type: BlockType; sys?: true }

const WorkflowBlockTriggerBlockChildrenSelector = ({
  block,
  path,
}: {
  block: WorkflowBlockChange
  path: string
}) => {
  const replicache = useRepoReplicache().data?.replicache
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const encodedSegment = useBranchOrCommitSegment().encodedSegment
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const selectedLayoutBlockId = block.value.trigger.layoutBlockId
  const addTab = useTabsStore((s) => s.addTab)
  const undoManager = useRepoUndoManager()
  const setFocus = useRepoStore((s) => s.setFocus)
  const setSelectedBlockId = useEditorStore((s) => s.setSelectedBlockId)
  const selectedLayoutBlock = useWorkingTreeStore((s) => {
    if (!s.tree || !block.value.trigger.layoutBlockId) return undefined
    return s.tree.getNode(block.value.trigger.layoutBlockId)?.value
  })
  const children = useWorkingTreeStore((s) => {
    if (!block.value.trigger.layoutBlockId) return
    const childrenKeys = s.tree?.getChildrenKeys(
      block.value.trigger.layoutBlockId
    )
    return childrenKeys
      ?.map((childId) => {
        const child = s.tree?.getNode(childId)
        if (!child) return null
        if (!isWorkflowTriggerBlockChangeCompatible(child.value)) return null
        return child
      })
      .filter(Boolean)
  })

  const onSelectBlock = async (
    selectedOption: (ComboboxOption & { type: BlockType }) | 'all'
  ) => {
    if (!replicache) return
    if (!block.value.trigger) return
    let newChildrenIds =
      selectedOption === 'all' ? [] : [...block.value.trigger.blockIds]
    if (selectedOption !== 'all') {
      if (block.value.trigger.blockIds.includes(selectedOption.value)) {
        newChildrenIds = newChildrenIds.filter(
          (id) => id !== selectedOption.value
        )
      } else {
        newChildrenIds.push(selectedOption.value)
      }
    }

    const newActions = Object.entries(block.value.actions).reduce(
      (acc, [key, action]) => {
        if (selectedOption === 'all') {
          acc[key] = action
          return acc
        }

        acc[key] = action

        return acc
      },
      {} as WorkflowBlock['value']['actions']
    )
    const params: UpdateBlockParams = {
      id: block.id,
      data: {
        type: 'workflow',
        value: {
          trigger: {
            ...block.value.trigger,
            blockIds: newChildrenIds,
          },
          actions: newActions,
        },
      },
      instanceChildTarget: block.instanceChildTarget,
      path,
      strategy: 'merge-deep',
      isoNow: new Date().toISOString(),
    }
    const { undo } = await replicache.mutate.updateBlock(params)
    undoManager.current?.push({
      redo: [{ op: 'updateBlock', args: params }],
      undo: [{ op: 'blockBatchMutations', args: { mutations: undo } }],
    })
  }

  let placeholder = undefined
  if (!block.value.trigger.layoutBlockId) {
    placeholder = 'First select a layout block above to continue'
  } else {
    const hasValueSet = block.value.trigger.blockIds.some((id) => getNode?.(id))
    if (!hasValueSet) {
      placeholder = 'Select which blocks will trigger the workflow'
    }
  }

  const onClear = React.useCallback(() => {
    if (!replicache) return
    if (!selectedLayoutBlockId) return
    const params: UpdateBlockParams = {
      id: block.id,
      data: {
        type: 'workflow',
        value: {
          trigger: {
            ...block.value.trigger,
            blockIds: [],
          },
        },
      },
      instanceChildTarget: block.instanceChildTarget,
      path,
      strategy: 'merge-deep',
      isoNow: new Date().toISOString(),
    }
    replicache.mutate.updateBlock(params)
  }, [
    block.id,
    block.instanceChildTarget,
    block.value.trigger,
    path,
    replicache,
    selectedLayoutBlockId,
  ])

  const [search, setSearch] = React.useState('')

  return (
    <Selector<ChildrenOption>
      sideOffset={8}
      extraWidth={4}
      search={search}
      setSearch={setSearch}
      placeholder={placeholder}
      className="flex-1"
      closeAfterSelect={false}
      icon={
        <span className="size-5 flex items-center justify-center pointer-events-none">
          <CaretSortIcon className="text-dark-muted pointer-events-none" />
        </span>
      }
      id={`${idToSourceBlockId(block)}-third-input`}
      size="sm"
      inputClassName="py-[0.1875rem] !gap-y-1"
      disabled={!selectedLayoutBlockId}
      onFocus={() => setSelectedBlockId(idToSourceBlockId(block))}
      onRemove={block.value.trigger.blockIds.length ? onClear : undefined}
      options={() => {
        if (!selectedLayoutBlockId) return []
        const title = {
          id: selectedLayoutBlockId,
          title: 'Title (sys)',
          sys: true,
          type: 'text' as const,
        }
        const childrenWithTitle = [title, ...(children ?? [])]
        return childrenWithTitle
          .map((childOrSysProp) => {
            if ('sys' in childOrSysProp) {
              if (block.value.trigger.blockIds.includes(childOrSysProp.id)) {
                return null
              }

              return {
                label: childOrSysProp.title,
                value: childOrSysProp.id,
                keywords: [
                  childOrSysProp.title,
                  childOrSysProp.type,
                  childOrSysProp.sys ? 'sys' : '',
                ],
                sys: true,
                type: childOrSysProp.type,
              } satisfies ChildrenOption
            }
            const child = childOrSysProp
            const sourceBlockId = idToSourceBlockId(child.value)
            if (block.value.trigger.blockIds.includes(sourceBlockId)) {
              return null
            }

            return {
              label: child.value.title || 'Untitled',
              value: sourceBlockId,
              keywords: [child.value.title || 'Untitled', child.value.type],
              type: child.value.type,
            }
          })
          .filter(Boolean)
      }}
      onKeyDown={(e) => {
        if (e.target instanceof HTMLInputElement && e.target.value) return
        if (!block.value.trigger.blockIds.at(-1)) return
        if (isHotkey(['delete', 'backspace'], e)) {
          onSelectBlock({
            value: block.value.trigger.blockIds.at(-1)!,
            label: 'Untitled',
            type: 'document',
          })
        } else if (isHotkey(['mod+delete', 'mod+backspace'], e)) {
          onSelectBlock('all')
        }
      }}
      onSelect={onSelectBlock}
      renderResult={() => {
        if (!getNode) return []
        return block.value.trigger.blockIds
          ?.map((bId) => {
            if (!getNode) return null
            const _block = getNode(bId)?.value ?? null
            if (!_block) return null
            const sourceBlockId = idToSourceBlockId(_block)
            if (!selectedLayoutBlockId) return null
            const isTitleSys = sourceBlockId === selectedLayoutBlockId
            return (
              <div
                key={sourceBlockId}
                className="-ml-[3px] flex gap-1 items-center border rounded border-dark-border-solid bg-dark-bg h-6 pl-1.5 pr-[3px]"
              >
                <ButtonLink
                  href={`/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${selectedLayoutBlockId}#${sourceBlockId}`}
                  unstyled
                  tabIndex={-1}
                  className="hover:underline flex items-center gap-1"
                  onClick={() => {
                    setFocus({
                      blockId: sourceBlockId,
                      type: isTitleSys
                        ? 'block:document-title'
                        : 'block:wrapper',
                    })
                    addTab({
                      key: selectedLayoutBlockId,
                      partiallyOpen: true,
                    })
                  }}
                >
                  <BlockIcon block={isTitleSys ? { type: 'text' } : _block} />
                  {isTitleSys ? 'Title (sys)' : _block.title || 'Untitled'}
                </ButtonLink>
                <Button
                  unstyled
                  className="text-dark-muted hover:text-dark-dim hover:bg-dark-shade-hover cursor-default rounded-sm size-4 flex items-center justify-center"
                  tabIndex={-1}
                  onClick={(e) => {
                    e.stopPropagation()
                    onSelectBlock({
                      value: sourceBlockId,
                      label: _block.title,
                      type: _block.type,
                    })
                  }}
                >
                  <Cross2Icon className="size-3" />
                </Button>
              </div>
            )
          })
          .filter(Boolean)
      }}
    >
      {({ search, options, getOptionProps, selectedIndex }) => {
        if (!options.length) {
          let emptyStateMessage = (
            <p className="text-ellipsis overflow-hidden h-10 flex items-center justify-center">
              <span className="font-medium">
                "{selectedLayoutBlock?.title || 'Untitled'}"
              </span>
              &nbsp;
              {block.value.trigger.blockIds.length
                ? 'has no further compatible children for this trigger'
                : 'has no compatible children for this trigger'}
            </p>
          )
          if (search) {
            emptyStateMessage = (
              <p className="text-ellipsis overflow-hidden h-10 flex items-center justify-center">
                No children blocks found for{' '}
                <span className="font-medium">"{search}"</span>
              </p>
            )
          }

          return (
            <div className="text-xs text-dark-muted text-center leading-normal py-[6.25px]">
              {emptyStateMessage}
            </div>
          )
        }

        return options.map((option, index) => {
          const isHighlighted = selectedIndex === index
          const b = getNode?.(option.value)?.value
          if (!b) return null
          return (
            <SelectorItem
              key={option.value}
              {...getOptionProps(index)}
              className={clsx(
                'flex items-center grid-cols-[auto_1fr] gap-x-2 h-8 scroll-mt-1 relative py-1.5 cursor-default select-none rounded px-2 text-xs leading-none outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                isHighlighted && 'bg-dark-shade-hover outline-none'
              )}
            >
              <BlockIcon block={option.sys ? { type: 'text' } : b} />
              <span className="text-xs text-dark-bright leading-normal truncate">
                {option.label}
              </span>
            </SelectorItem>
          )
        })
      }}
    </Selector>
  )
}
